import { ChatType, Message, TemporaryMessage } from '@/types/chat';
import { Side } from '@/types/user';

export const sortMessages = (
  channel: ChatType,
  messages: (Message | TemporaryMessage)[]
): (Message | TemporaryMessage)[] => {
  const existing = channel.messages || [];
  const allMessages = [...existing, ...messages];

  const uniqueMap = new Map<string, Message | TemporaryMessage>();

  for (const msg of allMessages) {
    uniqueMap.set(msg.id, msg);
  }

  const messagesWithoutDuplications = Array.from(uniqueMap.values());

  return messagesWithoutDuplications.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

export const updateMessageById = (
  channel: ChatType,
  message: Message | TemporaryMessage,
  updater: (
    oldMessage: Message | TemporaryMessage
  ) => Message | TemporaryMessage
) => {
  const existing = channel.messages || [];
  const index = existing.findIndex(m => m.id === message.id);

  if (index === -1) return channel;

  return [
    ...existing.slice(0, index),
    updater(existing[index]),
    ...existing.slice(index + 1)
  ];
};

export const addMessageToChannel = (
  channel: ChatType,
  message: Message | TemporaryMessage
) => {
  const existing = channel.messages || [];

  if (existing.some(m => m.id === message.id)) {
    return channel;
  }

  return [...existing, { ...message }];
};

export const makeReadMessages = (
  messages: (Message | TemporaryMessage)[],
  userId: string
): (Message | TemporaryMessage)[] => {
  return messages?.map(msg => {
    if (msg.senderId === userId && !msg.read) {
      return { ...msg, read: true };
    }

    return msg;
  });
};

export const getMessageWithoutDeleted = (
  messageId: string,
  messages: (Message | TemporaryMessage)[]
): (Message | TemporaryMessage)[] => {
  const existing = messages ?? [];
  return existing.filter((m: Message | TemporaryMessage) => m.id !== messageId);
};

export const handleLastMessage = (
  prevChannels: ChatType[],
  chatId: string,
  message: (Message | TemporaryMessage)[]
): ChatType[] => {
  prevChannels.map(channel => {
    if (channel.id === chatId) {
      return {
        ...channel,
        lastMessage: message
      };
    }

    return channel;
  });
};

export const addChannelsToBottom = (
  prevChannels: ChatType[],
  newChannels: ChatType[]
): ChatType[] => {
  const existingIds = new Set(prevChannels.map(channel => channel.id));
  const uniqueNewChannels = newChannels.filter(
    channel => !existingIds.has(channel.id)
  );
  return [...prevChannels, ...uniqueNewChannels];
};

export const addChannelToBottom = (
  prevChannels: ChatType[],
  newChannel: ChatType
): ChatType[] => {
  if (prevChannels.some(c => c.id === newChannel.id)) {
    return prevChannels;
  }

  return [newChannel, ...prevChannels];
};

export const moveChannelToTop = (
  prevChannels: ChatType[],
  chatId: string
): ChatType[] => {
  const index = prevChannels.findIndex(channel => channel.id === chatId);

  if (index === -1) return prevChannels;

  const updated = [...prevChannels];
  const [channel] = updated.splice(index, 1);
  updated.unshift(channel);

  return updated;
};

export const addChannelToTop = (
  prevChannels: ChatType[],
  newChannel: ChatType
): ChatType[] => {
  const exists = prevChannels.some(c => c.id === newChannel.id);

  if (exists) {
    return prevChannels.map(c => (c.id === newChannel.id ? newChannel : c));
  }

  return [newChannel, ...prevChannels];
};

export const replaceChannel = (
  prevChannels: ChatType[],
  storedId: string,
  newChannel: ChatType
): ChatType[] => {
  prevChannels.map(channel => {
    if (channel.id !== storedId) return channel;

    return {
      ...newChannel,
      messages: channel.messages
    };
  });
};

export const handleBlockStatus = (
  prevChannels: ChatType[],
  selfStatus: boolean,
  peerStatus: boolean,
  targetId: string
): ChatType[] => {
  const channel = prevChannels.find(c => c?.receiver?.id === targetId);

  if (
    !channel ||
    (channel?.selfBlock === selfStatus && channel?.peerBlock === peerStatus)
  ) {
    return prevChannels;
  }

  return prevChannels.map(c =>
    c?.receiver?.id === targetId
      ? { ...c, selfBlock: selfStatus, peerBlock: peerStatus }
      : c
  );
};

export const handleLeftStatus = (
  prevChannels: ChatType[],
  chatId: string,
  side: Side,
  leftAt: string
): ChatType[] => {
  return prevChannels.map(channel =>
    channel.id === chatId
      ? {
          ...channel,
          leftAtBy: {
            ...channel.leftAtBy,
            [side]: leftAt
          }
        }
      : channel
  );
};
